import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PhoneActions from "../components/PhoneActions";

const formatUnit = (value = "") => {
  const unit = String(value || "").trim();
  if (!unit || unit.toLowerCase() === "pcs" || unit.toLowerCase() === "unit") return "No";
  return unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase();
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const formatAddress = (address) =>
  [
    address?.street,
    address?.area,
    address?.city,
    address?.state,
    address?.pincode
  ].filter(Boolean).join(", ") || "-";

export default function DealerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [dealer, setDealer] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const dealerBasePath = location.pathname.startsWith("/admin") ? "/admin/dealers" : "/accounts/dealers";
  const materialStockPath = location.pathname.startsWith("/admin") ? "/admin/material-stock" : "/accounts/material-stock";

  useEffect(() => {
    const loadDealer = async () => {
      try {
        const res = await axios.get(API_BASE_URL + `/api/dealers/${id}`);
        setDealer(res.data.dealer || null);
        setPurchases(Array.isArray(res.data.purchases) ? res.data.purchases : []);
      } catch (error) {
        alert("Failed to load supplier");
      } finally {
        setLoading(false);
      }
    };

    loadDealer();
  }, [id]);

  const totals = useMemo(() => {
    return purchases.reduce((acc, item) => {
      acc.quantity += Number(item.quantity || 0);
      return acc;
    }, { quantity: 0 });
  }, [purchases]);

  if (loading) {
    return <div className="p-6">Loading supplier...</div>;
  }

  if (!dealer) {
    return <div className="p-6">Supplier not found.</div>;
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-amber-200 bg-[linear-gradient(135deg,_rgba(255,251,235,1),_rgba(255,255,255,1),_rgba(254,243,199,0.9))] p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-amber-700">
              Supplier Profile
            </p>
            <h1 className="mt-2 text-3xl font-bold text-stone-950">{dealer.companyName || dealer.name}</h1>
            <p className="mt-2 text-sm text-stone-500">
              {dealer.supplierCode || dealer.dealerId || "Supplier"} • Created {formatDate(dealer.createdAt)}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(`${dealerBasePath}/${dealer._id}/edit`)}
              className="rounded-2xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-stone-950 transition hover:bg-amber-400"
            >
              Edit Supplier
            </button>
            <button
              onClick={() => navigate(dealerBasePath)}
              className="rounded-2xl bg-stone-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black"
            >
              Back To Suppliers
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <MetricCard label="Purchase Entries" value={purchases.length} />
          <MetricCard label="Total Bought Qty" value={totals.quantity} />
          <MetricCard label="Contact" value={dealer.name || "-"} />
          <MetricCard label="GST" value={dealer.gstNumber || "-"} />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr,1.2fr]">
        <div className="rounded-[28px] border border-amber-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-stone-950">Supplier Details</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <DetailCard label="Contact Name" value={dealer.name || "-"} />
            <DetailCard label="Company" value={dealer.companyName || "-"} />
            <DetailCard label="Supplier Code" value={dealer.supplierCode || "-"} />
            <DetailCard label="Phone" value={<PhoneActions phone={dealer.phone || ""} />} />
            <DetailCard label="Email" value={dealer.email || "-"} />
            <DetailCard label="GST Number" value={dealer.gstNumber || "-"} />
            <DetailCard label="Address" value={formatAddress(dealer.address)} />
          </div>
          <div className="mt-4 rounded-3xl border border-stone-200 bg-stone-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-400">Notes</p>
            <p className="mt-2 text-sm leading-6 text-stone-700">{dealer.notes || "-"}</p>
          </div>
        </div>

        <div className="rounded-[28px] border border-amber-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-stone-950">Things We Bought</h2>
            </div>
            <button
              type="button"
              onClick={() => navigate(materialStockPath)}
              className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-stone-950"
            >
              Open Stock
            </button>
          </div>

          {purchases.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-dashed border-stone-300 bg-stone-50 px-4 py-10 text-center text-stone-500">
              No purchase history for this supplier yet.
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[760px] text-left">
                <thead className="bg-stone-100">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Material</th>
                    <th className="px-4 py-3">Quantity</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((entry) => (
                    <tr key={entry._id} className="border-t border-stone-200">
                      <td className="px-4 py-3 text-sm text-stone-600">{formatDate(entry.transactionDate)}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-stone-900">{entry.itemName}</p>
                        <p className="text-xs text-stone-500">{entry.sku || "No SKU"}</p>
                      </td>
                      <td className="px-4 py-3 font-semibold text-stone-900">{entry.quantity} {formatUnit(entry.unit)}</td>
                      <td className="px-4 py-3 text-sm text-stone-700">{entry.location || "-"}</td>
                      <td className="px-4 py-3 text-sm text-stone-700">{entry.reference || "-"}</td>
                      <td className="px-4 py-3 text-sm text-stone-600">{entry.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-3xl border border-white/80 bg-white/80 p-5 shadow-sm">
      <p className="text-sm font-semibold text-stone-600">{label}</p>
      <p className="mt-3 text-2xl font-bold text-stone-950">{value}</p>
    </div>
  );
}

function DetailCard({ label, value }) {
  return (
    <div className="rounded-3xl border border-stone-200 bg-stone-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-400">{label}</p>
      <div className="mt-2 text-sm leading-6 text-stone-700">{value}</div>
    </div>
  );
}


